import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { InsideSheetContext } from "./sheetContext";

/**
 * When the last sheet on the page closed, so the next one can tell whether it
 * is taking over from it — see `handoff` below. Module-wide rather than per
 * sheet, because the two are different components: a record's sheet closes
 * and the form's opens, and neither knows the other is there.
 */
let lastClosedAt = -Infinity;

/**
 * How close behind another sheet's closing an opening has to be to count as
 * taking over from it. A swap happens inside one commit, so the gap is a few
 * milliseconds; anything a person could have pressed in between is far past
 * this.
 */
const HANDOFF_MS = 100;

/**
 * How far down the sheet has to be dragged for the release to dismiss it
 * rather than spring it back.
 *
 * A sheet is dismissed by flicking it off the bottom edge, which nobody does
 * carefully — but it is also the gesture a thumb makes by accident while
 * reaching for the first row, so the threshold has to be past a reach. 96px is
 * about a thumb's travel from the grabber to the top of the body.
 */
const DRAG_DISMISS_PX = 96;

/**
 * Or a flick: downward pixels per millisecond.
 *
 * A short fast drag is as clear an answer as a long slow one, and waiting for
 * the full 96px is what makes a sheet feel stuck to the screen. Guarded by a
 * few pixels of travel below, so the jitter in a tap is not a flick.
 */
const DRAG_DISMISS_VELOCITY = 0.5;

/** Below this, a fast movement is a tap that wobbled rather than a flick. */
const FLICK_FLOOR_PX = 24;

export interface BottomSheetProps {
  open: boolean;
  /** Drawn as the sheet's heading and used as its accessible name. */
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /**
   * Actions pinned below the body, which scrolls under them. For a sheet that
   * is asking for something rather than telling — a form, or a record with
   * something to do to it — where the way to finish must not be somewhere
   * further down.
   *
   * They are stretched to fill the row: a sheet is as wide as the phone, and
   * two buttons sized by their own words leave most of that width empty and
   * neither of them where the thumb is.
   *
   * Stretched rather than halved, which is what they used to be. An `md`
   * button carries 24px of padding a side, so half a phone's row leaves about
   * 100px for the words — and "Request payment" broke across two lines inside
   * a button the same height as the one saying "Cancel". Each one now starts
   * from the width of its own label and grows into what is left over, so the
   * row is still full, both are still under the thumb, and the longer label
   * gets the room it needs rather than an equal share of a row it does not
   * fit in.
   */
  footer?: React.ReactNode;
  /**
   * Drops the heading, leaving the panel to whatever `children` draws — for a
   * sheet whose whole message is its body, like a first-run welcome that leads
   * with a mark and a headline. `title` still names it to a screen reader, so
   * this is never a reason to leave it off. `Modal`'s prop of the same name,
   * for the same case on the other shell.
   */
  hideTitle?: boolean;
  /**
   * Whether a press on the dimming closes it. On by default, which is what a
   * sheet should do — it is the way out a thumb reaches for first.
   *
   * Turn it off for a sheet that is asking rather than showing, where the only
   * showing there was would be spent by a stray press. Escape still closes it
   * either way: a dialog with no way out is a trap for anyone on a keyboard.
   * Again `Modal`'s prop, so the two shells answer a press outside the same
   * way.
   *
   * Nothing turns it off today, and that is the decision rather than an
   * oversight: a drawer that cannot be pressed away is the one thing a phone
   * does not expect, and every sheet can be dragged off the bottom edge now
   * besides. It is left here for the dialog `Modal` still needs it for.
   */
  dismissOnBackdrop?: boolean;
  /**
   * Surface override for the panel — a tinted fill under a welcome, and
   * nothing else. Layout is the sheet's own: it is as wide as the phone and
   * anchored to the bottom edge, which is what makes it a sheet.
   */
  className?: string;
}

/**
 * A panel that comes up from the bottom edge of the screen, rendered into
 * `document.body`.
 *
 * The phone's answer to a menu that would otherwise be pinned beside whatever
 * opened it: a list anchored to a control near the top of a phone opens over
 * the very content it is filtering, and is read at arm's length from the thumb
 * doing the reaching, while a sheet arrives under that thumb and can be as tall
 * as it needs. So this is not a `Modal` with different corners — it is where a
 * phone puts a choice, and it stands in below `md` for the panels that suit a
 * mouse.
 *
 * The portal matters for the same reason it does in `Modal`: the shell pages
 * are `overflow-hidden` flex columns, so a sheet left inside the tree would be
 * clipped by whichever card it was opened from.
 */
const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  title,
  onClose,
  children,
  footer,
  hideTitle = false,
  dismissOnBackdrop = true,
  className = "",
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  // Whatever had focus before the sheet opened, so it can be handed back on
  // close instead of dropping focus to the top of the document.
  const opener = useRef<HTMLElement | null>(null);

  // Escape is read through a ref rather than closed over, so the effect below
  // can depend on `open` alone — callers pass an inline arrow, whose identity
  // changes every render, and a set-up and tear-down per render would hand
  // focus back to the opener with the sheet still up. Same reasoning as
  // `Modal`, which had exactly that bug.
  /**
   * How far down the sheet has been dragged, in pixels, and whether a thumb
   * is still on it.
   *
   * Two pieces rather than one, because they do different work: the offset is
   * what the panel is drawn at, and `held` is what decides whether it follows
   * the thumb exactly or eases back. A single transition on the transform
   * would lag a drag by its own duration, which reads as the sheet being
   * dragged through treacle.
   */
  const [drag, setDrag] = useState(0);
  const [held, setHeld] = useState(false);

  /**
   * Where the thumb went down, and when — the two the release is judged on.
   *
   * The clock is `performance.now()` rather than the event's own `timeStamp`:
   * the two agree to within a frame, and this file already keeps a stamp from
   * that clock for the handoff above, so there is one notion of "now" here.
   */
  const from = useRef<{ y: number; at: number } | null>(null);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  /**
   * Whether this sheet is taking over from one that closed as it opened —
   * a service's sheet handing to the form that edits it, or the form handing
   * back once it has saved.
   *
   * Drawn differently when it is: the dimming behind is already there, so it
   * does not fade in again, and the panel settles in from just under where
   * the last one stood rather than climbing up from the bottom edge — so the
   * drawer reads as changing what it holds rather than as closing and
   * reopening, which is a flicker.
   *
   * Decided in a layout effect rather than in render, because that is the one
   * place the order is known: within a commit every sheet closing runs its
   * cleanup before any sheet opening runs its effect, so the stamp the closing
   * one leaves is there for the opening one to read. Set as state from there,
   * which re-renders before the browser paints — the first frame drawn is
   * already the right one.
   */
  const [handoff, setHandoff] = useState(false);
  useLayoutEffect(() => {
    if (!open) return;
    setHandoff(performance.now() - lastClosedAt < HANDOFF_MS);

    const panel = panelRef.current;
    return () => {
      // Only once the sheet is really gone — the same test the focus effect
      // below makes, for the same reason: in development React runs this
      // cleanup and sets the effect straight back up with the panel still on
      // screen, and a stamp left then would make every ordinary opening read
      // as a handoff.
      if (!panel?.isConnected) lastClosedAt = performance.now();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };

    opener.current = document.activeElement as HTMLElement | null;
    document.addEventListener("keydown", handleKeyDown);

    // A sheet that was dragged a little and sprang back is reopened at rest.
    // This component is not unmounted when it closes — it returns null — so
    // the offset would otherwise be whatever the last drag left behind.
    from.current = null;
    setHeld(false);
    setDrag(0);

    // The page behind must not scroll under the backdrop.
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    // The panel rather than the first row: the heading is what should be
    // announced first, and the rows are one tab away.
    const panel = panelRef.current;
    panel?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = overflow;

      // Only once the sheet is really gone. In development React runs this
      // cleanup and sets the effect straight back up with the panel still on
      // screen, and handing focus back then pulls it out from under a sheet
      // the reader is still looking at.
      if (!panel?.isConnected) opener.current?.focus();
    };
  }, [open]);

  /**
   * The drag, from the grabber and the heading under it.
   *
   * That strip rather than the whole panel, and not because a sheet should
   * only be draggable by its handle: the body scrolls, and holds fields and
   * chips and a date picker. A drag started on any of those is either a scroll
   * or a selection, and a sheet that read it as a dismissal would be taking
   * the gesture away from the control that was actually under the thumb. The
   * strip is the one part of a sheet that holds nothing.
   *
   * `touch-action: none` on it is what stops the browser scrolling the page or
   * firing pull-to-refresh instead of handing these events over.
   */
  const handleDragStart = (event: React.PointerEvent<HTMLDivElement>) => {
    // The primary button only: a right-click is not a drag.
    if (event.button !== 0) return;
    from.current = { y: event.clientY, at: performance.now() };
    setHeld(true);
    // So a thumb that leaves the strip — which it does immediately, since the
    // strip moves with it — keeps sending its moves here.
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleDragMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!from.current) return;
    // Downward only. A sheet is already as far up as it goes, and letting it
    // be pulled past the top edge would be an answer to a gesture that means
    // nothing.
    setDrag(Math.max(0, event.clientY - from.current.y));
  };

  const handleDragEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = from.current;
    if (!start) return;
    from.current = null;
    // Asked first: releasing a pointer the element does not hold throws, and
    // a cancelled gesture is one the browser has already taken back.
    if (event.currentTarget.hasPointerCapture?.(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);

    const travelled = Math.max(0, event.clientY - start.y);
    const elapsed = Math.max(1, performance.now() - start.at);
    const flicked =
      travelled > FLICK_FLOOR_PX &&
      travelled / elapsed > DRAG_DISMISS_VELOCITY;

    setHeld(false);
    setDrag(0);

    // Dismissed rather than animated out: nothing else about this sheet has an
    // exit — a press on the dimming unmounts it where it stands — and a drag
    // that is already most of the way down has drawn its own.
    if (travelled > DRAG_DISMISS_PX || flicked) onClose();
  };

  const titleId = `${React.useId()}-sheet-title`;

  if (!open) return null;

  return createPortal(
    // Everything below is inside a sheet, and one thing cares: a picker cannot
    // answer a field in here by opening a second sheet under this one. See
    // `sheetContext`.
    <InsideSheetContext.Provider value={true}>
      {/* Closes on a press that both starts and ends on the backdrop, so a
          drag that begins inside the sheet and releases outside does not
          dismiss it. */}
      <div
        // Dimmed and softly blurred rather than dimmed alone: the blur is what
        // pushes the screen behind into the background instead of leaving it
        // legible under a grey wash, and it fades in with the sheet rather than
        // snapping on ahead of it. Not on a handoff, where the last sheet's
        // dimming was on screen a moment ago: fading it in again would flash
        // the page behind between the two.
        className={`fixed inset-0 z-50 flex items-end justify-center bg-[#0B1B1F]/45 backdrop-blur-[3px] ${
          handoff ? "" : "animate-fade-in"
        }`}
        onMouseDown={(event) => {
          if (!dismissOnBackdrop) return;
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          // Never taller than most of the screen: a sheet that reaches the top
          // edge is a page, and the strip of the list still showing above it is
          // what says the screen behind is there to come back to. The safe-area
          // inset keeps the last row clear of the home indicator.
          //
          // The corners are 16px, and deliberately rounder than the 8px a card
          // takes on the same screen: a sheet is a layer over the app rather
          // than another panel in the stack, and the corner is what says so as
          // it comes up off the edge. It is the one radius every drawer in the
          // app is drawn with; they all come through here. Written out rather
          // than taken from `card-surface`, which is a card's corner and has
          // been changed twice without this needing to move.
          // `className` last, so a caller's fill paints over `bg-white`
          // rather than depending on which of two utilities Tailwind emits
          // last — same order, and same reason, as `Modal`.
          // The entrance animation goes the moment a thumb is on it: that
          // animation drives `transform`, and a keyframe still running would
          // win over the offset below and leave the sheet ignoring the drag.
          className={`flex w-full max-w-[560px] max-h-[85vh] flex-col overflow-hidden rounded-t-[16px] bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-16px_48px_rgba(11,27,31,0.24)] focus:outline-none ${
            held || drag ? "" : handoff ? "animate-sheet-swap" : "animate-sheet-in"
          } ${className}`}
          style={{
            transform: drag ? `translateY(${drag}px)` : undefined,
            // Exactly under the thumb while it is held, and eased back once it
            // lets go — the same curve the sheet arrives on.
            transition: held
              ? undefined
              : "transform 220ms cubic-bezier(0.32, 0.72, 0, 1)",
          }}
        >
          {/* The grabber and the heading under it, which together are what the
            sheet is dragged by — see `handleDragStart`. The bar itself is
            decoration and stays unannounced: a drag is not the only way out
            and never the only way in, since a press on the dimming and Escape
            both close the sheet without it. */}
          <div
            className="flex-shrink-0 cursor-grab touch-none select-none active:cursor-grabbing"
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            // A gesture the system takes over — a notification pulled down
            // over the app, a call arriving — ends the drag where it stands
            // rather than leaving the sheet stuck to a thumb that is gone.
            onPointerCancel={handleDragEnd}
          >
            <div aria-hidden="true" className="flex justify-center pt-3 pb-1">
              <span className="h-[5px] w-10 rounded-full bg-[#DFE6EA]" />
            </div>

            <h2
              id={titleId}
              className={
                hideTitle
                  ? "sr-only"
                  : "px-5 pb-1 pt-2.5 text-[18px] font-semibold tracking-[-0.01em] text-ink"
              }
            >
              {title}
            </h2>
          </div>

          {/* A small gap before the footer's rule, or the same 20px the footer
            keeps where there is none — a sheet that only shows a record ends
            on its last row, and that row wants the room the buttons would
            have had. */}
          <div
            className={`min-h-0 flex-1 overflow-y-auto ${
              footer ? "pb-2" : "pb-5"
            }`}
          >
            {children}
          </div>

          {/* Held out of the scroll, so a form long enough to scroll still has
            its Save on screen — and so the last field typed into is never the
            thing hiding it. The rule above is what says the row is not part of
            what is being read.

            20px under the buttons, which is what the tab bar keeps under its
            labels — 8px inside the tab and 12px under the bar — so a sheet's
            actions end where the app's own bottom row ends. The panel's
            safe-area inset is added below this and clears the home indicator
            on top of it; the 20px is spelled out rather than left to that
            inset, which is 0px on a phone reporting none and was leaving these
            buttons on the very edge of the screen. */}
          {footer && (
            <div className="flex-shrink-0 flex items-center gap-3 border-t border-[#F1F4F6] px-5 pb-5 pt-3 [&>*]:flex-auto [&>*]:whitespace-nowrap">
              {footer}
            </div>
          )}
        </div>
      </div>
    </InsideSheetContext.Provider>,
    document.body
  );
};

export default BottomSheet;
